/** Light / dark / system theme. `data-theme` on <html> overrides prefers-color-scheme. */
import { useSyncExternalStore } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'nova.theme';
const listeners = new Set<() => void>();

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

let pref: ThemePref = typeof window !== 'undefined' ? read() : 'system';

function apply(): void {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

export function setThemePref(next: ThemePref): void {
  pref = next;
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    /* private mode */
  }
  apply();
  listeners.forEach((fn) => fn());
}

export function getThemePref(): ThemePref {
  return pref;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener?.('change', fn);
  return () => {
    listeners.delete(fn);
    mq?.removeEventListener?.('change', fn);
  };
}

export function useThemePref(): ThemePref {
  return useSyncExternalStore(subscribe, getThemePref, () => 'system');
}

function resolved(): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The theme actually shown (for the map style and chart theme). */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribe, resolved, () => 'light');
}

/** Read a token's computed value (e.g. to hand a colour to ECharts or store a swatch). */
export function readToken(name: `--${string}`): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
